import { useState, useEffect, useCallback } from 'react';
import heic2any from 'heic2any';
import './FileViewer.css';

/**
 * A component for viewing files of various types
 * Handles special cases like HEIC files that browsers don't natively support
 */
const FileViewer = ({ fileUrl, fileType, fileName }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConverting, setIsConverting] = useState(false);
  const [convertedUrl, setConvertedUrl] = useState(null);

  // Use useCallback to memoize the handleHeicFile function
  const handleHeicFile = useCallback(async () => {
    try {
      console.log('Converting HEIC/HEIF file to JPEG...');
      setIsConverting(true);

      // Fetch the HEIC file as a blob
      const response = await fetch(fileUrl, {
        cache: 'no-store',  // Prevent caching issues
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch HEIC file: ${response.status} ${response.statusText}`);
      }

      const heicBlob = await response.blob();
      console.log('HEIC blob size:', heicBlob.size, 'bytes');

      if (heicBlob.size === 0) {
        throw new Error('Empty file received');
      }

      // Convert HEIC to JPEG using heic2any
      console.log('Starting HEIC conversion...');
      const jpegBlob = await heic2any({
        blob: heicBlob,
        toType: 'image/jpeg',
        quality: 0.8
      });
      console.log('HEIC conversion complete');

      // Create a URL for the converted JPEG
      const jpegUrl = URL.createObjectURL(jpegBlob);
      console.log('Created object URL for converted image');
      setConvertedUrl(jpegUrl);
      setIsConverting(false);
    } catch (error) {
      console.error('Error converting HEIC file:', error);
      setIsLoading(false);
      setIsConverting(false);
      setError({
        title: 'HEIC/HEIF Format Detected',
        message: 'We tried to convert this HEIC/HEIF file for viewing, but encountered an error. You can download the file to view it with your device\'s native image viewer.'
      });
      // Don't re-throw the error, just handle it here
    }
  }, [fileUrl]); // Add fileUrl as a dependency

  useEffect(() => {
    // Skip effect if fileUrl is not available yet
    if (!fileUrl) {
      console.log('No fileUrl available yet, skipping effect');
      return;
    }

    console.log('FileViewer effect running with fileUrl:', fileUrl);
    setIsLoading(true);
    setError(null);
    setConvertedUrl(null);

    // Handle HEIC files
    if (fileType && (fileType.includes('heic') || fileType.includes('heif'))) {
      console.log('HEIC/HEIF file detected, starting conversion');
      const convertFile = async () => {
        try {
          await handleHeicFile();
          setIsLoading(false);
        } catch (error) {
          console.error('Error in conversion effect:', error);
          setIsLoading(false);
        }
      };

      convertFile();
    } else {
      console.log('Non-HEIC file, setting loading to false');
      setIsLoading(false);
    }

    // Cleanup function to revoke object URLs when component unmounts or when fileUrl changes
    return () => {
      if (convertedUrl) {
        console.log('Cleaning up converted URL');
        URL.revokeObjectURL(convertedUrl);
        setConvertedUrl(null);
      }
    };
  }, [fileUrl, fileType, handleHeicFile]); // Include handleHeicFile in dependencies

  const renderFileContent = () => {
    if (isLoading) {
      return <div className="file-viewer-loading">Loading file...</div>;
    }

    if (isConverting) {
      return (
        <div className="file-viewer-converting">
          <div className="converting-spinner"></div>
          <p>Converting HEIC/HEIF image for viewing...</p>
          <p className="converting-note">This may take a few moments depending on the file size.</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="file-viewer-error">
          <h3>{error.title}</h3>
          <p>{error.message}</p>
          <a href={fileUrl} download={fileName} className="download-button">
            Download File
          </a>
        </div>
      );
    }

    // Use the converted URL if available, otherwise use the original URL
    const displayUrl = convertedUrl || fileUrl;

    // Render based on file type
    if (fileType.startsWith('image/')) {
      return (
        <div className="image-container">
          <img src={displayUrl} alt={fileName} className="file-viewer-image" />
          {/* Conversion notice removed as requested */}
        </div>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <iframe
          src={`${displayUrl}#toolbar=0&navpanes=0`}
          title={fileName}
          className="file-viewer-pdf"
        />
      );
    } else if (fileType.startsWith('video/')) {
      return (
        <video controls className="file-viewer-video">
          <source src={displayUrl} type={fileType} />
          Your browser does not support the video tag.
        </video>
      );
    } else if (fileType.startsWith('audio/')) {
      return (
        <audio controls className="file-viewer-audio">
          <source src={displayUrl} type={fileType} />
          Your browser does not support the audio tag.
        </audio>
      );
    } else {
      // For other file types, show a download button
      return (
        <div className="file-viewer-unsupported">
          <p>This file type cannot be previewed in the browser.</p>
          <a href={fileUrl} download={fileName} className="download-button">
            Download File
          </a>
        </div>
      );
    }
  };

  return (
    <div className="file-viewer-container">
      {renderFileContent()}
    </div>
  );
};

export default FileViewer;
